import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/i18n";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function Ajustes() {
    const { user, updateUser, logout } = useAuth();
    const { locale, setLocale, t } = useT();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        name: user?.name || "",
        company: user?.company || "",
        story: user?.story || "",
        starting_point: user?.starting_point || "networker",
        goal: user?.goal || "ambos",
    });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.put("/auth/profile", {
                name: form.name.trim(),
                company: form.company?.trim() || null,
                story: form.story?.trim() || null,
                starting_point: form.starting_point,
                goal: form.goal,
            });
            updateUser(data.user);
            toast.success("Cambios guardados");
        } catch (err) { toast.error(errMsg(err)); }
        finally { setSaving(false); }
    };

    const deleteAccount = async () => {
        try {
            await api.delete("/auth/account");
            await logout();
            toast.success("Tu cuenta se ha eliminado");
            navigate("/");
        } catch (e) { toast.error(errMsg(e)); }
    };

    return (
        <div className="max-w-2xl space-y-8" data-testid="ajustes-page">
            <div>
                <h1 className="text-3xl font-bold tracking-tight text-nm-ink">Ajustes</h1>
                <p className="mt-1 text-nm-muted">Tu perfil y tus objetivos. Puedes cambiarlos cuando quieras.</p>
            </div>

            <form onSubmit={save} className="space-y-6 rounded-2xl border border-nm-line bg-nm-surface p-6">
                <div className="space-y-1.5">
                    <Label htmlFor="name">Tu nombre</Label>
                    <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="settings-name" />
                </div>
                <div className="space-y-1.5">
                    <Label>Correo</Label>
                    <Input value={user?.email} disabled className="bg-nm-bg" />
                </div>
                <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-1.5">
                        <Label>¿Desde dónde partes?</Label>
                        <Select value={form.starting_point} onValueChange={(v) => set("starting_point", v)}>
                            <SelectTrigger data-testid="settings-start"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="desde_cero">Empiezo desde cero</SelectItem>
                                <SelectItem value="networker">Ya soy networker</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5">
                        <Label>¿Qué quieres conseguir?</Label>
                        <Select value={form.goal} onValueChange={(v) => set("goal", v)}>
                            <SelectTrigger data-testid="settings-goal"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="clientes">Captar clientes</SelectItem>
                                <SelectItem value="equipo">Construir equipo</SelectItem>
                                <SelectItem value="ambos">Las dos cosas</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="company">Empresa o producto</Label>
                    <Input id="company" value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="settings-company" />
                </div>
                <div className="space-y-1.5">
                    <Label htmlFor="story">Tu historia en una frase</Label>
                    <Textarea id="story" rows={3} value={form.story} onChange={(e) => set("story", e.target.value)} data-testid="settings-story" />
                </div>
                <div className="flex justify-end">
                    <Button type="submit" disabled={saving} data-testid="settings-save">
                        {saving ? "Guardando..." : "Guardar cambios"}
                    </Button>
                </div>
            </form>

            {/* Idioma */}
            <div className="rounded-2xl border border-nm-line bg-nm-surface p-6" data-testid="language-card">
                <h2 className="text-lg font-semibold text-nm-ink">{t("settings.language")}</h2>
                <p className="mt-2 text-sm text-nm-muted">{t("settings.language.help")}</p>
                <div className="mt-4 w-full sm:w-56">
                    <Select value={locale} onValueChange={setLocale}>
                        <SelectTrigger data-testid="language-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="es">Español</SelectItem>
                            <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* Datos y privacidad */}
            <div className="rounded-2xl border border-nm-line bg-nm-surface p-6">
                <h2 className="text-lg font-semibold text-nm-ink">Tus datos</h2>
                <p className="mt-2 text-sm leading-relaxed text-nm-muted">
                    Solo guardamos lo necesario para que la herramienta funcione: tu nombre, tu correo,
                    tus contactos y tu progreso. Puedes eliminar tu cuenta y todos tus datos cuando quieras.
                    Esta acción es un borrado y no se podrá recuperar.
                </p>
                <div className="mt-5">
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <Button variant="outline" className="text-destructive hover:text-destructive" data-testid="delete-account-button">
                                Eliminar mi cuenta y mis datos
                            </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="rounded-2xl">
                            <AlertDialogHeader>
                                <AlertDialogTitle>¿Eliminar tu cuenta?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Se eliminarán tu perfil y todos tus contactos. No podrás deshacer esta acción.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction onClick={deleteAccount} data-testid="confirm-delete-account">Sí, eliminar todo</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </div>
    );
}
